import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useKindReview, useRecordKind, type KindChoice, type KindReviewRow } from '../api/kindReview.ts';
import { ApiError } from '../api/client.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { formatDate } from '../dates.ts';

const primary =
  'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white ' +
  'hover:bg-neutral-800 disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

/** What the last recording did, said in words: the row itself leaves the list. */
interface Recorded {
  name: string;
  kind: string;
  owes: string;
}

/**
 * Appointments whose kind was assumed (038's v_appointment_kind_unreviewed).
 *
 * **Why it is a launch item** (Arthur, 25 September). 038 recorded every
 * appointment as a guardianship. A guardian owes four visits a year by statute
 * (MHL § 81.20(a)(5)); a trustee, a Special Court Evaluator and a guardian ad
 * litem do not. So until each is read and recorded, a trustee-only client shows
 * as a guardianship in visit arrears.
 *
 * Live clients first; closed clients below, marked (Arthur, 23 September: the
 * statement of death and the final account depend on the kind). Recording any
 * answer, Guardian included, takes the row off the list. A visit exemption is
 * not changed here: that is decided after the kind is right.
 */
export function KindReview() {
  const list = useKindReview();
  const [recorded, setRecorded] = useState<Recorded | null>(null);
  const rows = list.data?.appointments ?? [];
  const live = rows.filter((r) => !r.closed);
  const closed = rows.filter((r) => r.closed);

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Appointments to check</h1>
          <p className="mt-0.5 max-w-[64rem] text-sm text-neutral-500">
            Every appointment here was recorded as a guardianship by assumption, and its scope could
            equally be something else. A guardian owes at least four visits a year (MHL § 81.20(a)(5));
            a trustee, a Special Court Evaluator and a guardian ad litem owe none. Read the order and
            record what the appointment is. Recording Guardian is an answer too.
          </p>
        </header>
        <div className="page-body">
          {list.error && !list.data ? (
            list.error instanceof ApiError && list.error.status === 403 ? (
              <div role="alert" className="p-6">
                <h2 className="text-lg font-bold text-neutral-900">Not open to your roles</h2>
                <p className="mt-2 text-neutral-700">{list.error.message}</p>
                <Link to="/clients" className={`${primary} mt-5 inline-flex items-center`}>Back to clients</Link>
              </div>
            ) : (
              <LoadFailure what="The appointments to check" error={list.error}
                           onRetry={() => void list.refetch()} retrying={list.isFetching} />
            )
          ) : !list.data ? (
            <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-neutral-600">
              {recorded && <span className="block">{recorded.name}: recorded as {recorded.kind}. {recorded.owes}</span>}
              Every appointment's kind has been recorded.
            </p>
          ) : (
            <>
              {recorded && (
                <p role="status" className="mx-4 mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900 lg:mx-5">
                  <span className="font-semibold">{recorded.name}</span>: recorded as {recorded.kind}. {recorded.owes}
                </p>
              )}
              <p className="px-4 pt-3 text-sm text-neutral-700 lg:px-5">
                {list.data.live} for clients under guardianship now, {list.data.closed} for closed
                clients.
              </p>
              <ul className="divide-y divide-neutral-100">
                {live.map((r) => <Appointment key={r.appointment_id} r={r} kinds={list.data.kinds} onRecorded={setRecorded} />)}
              </ul>
              {closed.length > 0 && (
                <>
                  <h2 className="border-t-4 border-neutral-200 px-4 pt-4 pb-1 text-sm font-bold tracking-[0.1em]
                                 text-neutral-500 uppercase lg:px-5">
                    Closed clients
                  </h2>
                  <p className="px-4 text-sm text-neutral-600 lg:px-5">
                    Deceased or discharged. They owe no visits, but the statement of death and the
                    final account depend on what the appointment was.
                  </p>
                  <ul className="divide-y divide-neutral-100">
                    {closed.map((r) => <Appointment key={r.appointment_id} r={r} kinds={list.data.kinds} onRecorded={setRecorded} />)}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** What recording a kind does, in words, from the catalogue's own flags. */
function consequence(k: KindChoice): string {
  const parts = [
    k.owes_visits ? 'owes at least four visits a year' : 'owes no visits',
    k.needs_commission ? 'needs a commission' : 'needs no commission',
  ];
  if (!k.holds_a_side) parts.push('holds neither side, so any person or property role recorded is cleared');
  return parts.join('; ');
}

function Appointment({ r, kinds, onRecorded }: {
  r: KindReviewRow;
  kinds: KindChoice[];
  onRecorded: (done: Recorded) => void;
}) {
  const record = useRecordKind();
  const [chosen, setChosen] = useState('');
  const owes =
    r.visits_owed_now === null ? null
      : r.visits_owed_now === 0 ? `owes no visits now (${r.visit_source ?? 'no reason given'})`
        : `owes ${r.visits_owed_now} visits a year now (${r.visit_source ?? 'no source'})`;

  return (
    <li className="px-4 py-4 lg:px-5">
      {r.closed && (
        <p className="text-xs font-bold tracking-[0.1em] uppercase text-neutral-500">Closed · {r.legal_status}</p>
      )}
      <p className="font-semibold text-neutral-900">
        <Link to={`/clients/${r.client_id}`} state={{ from: 'Appointments to check' }}
              className="underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-900">
          {r.display_name}
        </Link>
        <span className="ml-2 font-normal text-neutral-500">{r.client_ref}</span>
      </p>
      <p className="text-sm text-neutral-700">
        Scope from the orders: <span className="font-medium">{r.scope_label ?? 'none on record'}</span>
        {' · '}recorded as {r.kind_label}, by assumption
        {r.is_temporary && ' · temporary'}
        {r.start_date && ` · from ${formatDate(r.start_date)}`}
        {r.index_number && ` · index ${r.index_number}`}
      </p>
      {r.why && <p className="mt-1 text-sm text-neutral-700">{r.why}</p>}
      {!r.closed && owes && (
        <p className="mt-1 text-sm text-neutral-600">
          This client {owes}{r.visit_exempt && ', and is marked visit-exempt'}.
        </p>
      )}

      <fieldset className="mt-2">
        <legend className="text-sm font-semibold text-neutral-700">What is this appointment?</legend>
        <div className="mt-1 grid gap-1 lg:grid-cols-2">
          {kinds.map((k) => (
            <label key={k.code}
                   className={`flex min-h-[44px] cursor-pointer gap-3 rounded-lg border px-3 py-2 ${
                     chosen === k.code ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'}`}>
              <input type="radio" name={`kind-${r.appointment_id}`} checked={chosen === k.code}
                     onChange={() => setChosen(k.code)} className="mt-1 size-5 shrink-0" />
              <span>
                <span className="font-medium text-neutral-900">{k.label}</span>
                <span className="block text-sm text-neutral-600">{consequence(k)}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {record.error && (
        <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
          {record.error.message}
          {record.error instanceof ApiError && record.error.hint && (
            <span className="block text-rose-700">{record.error.hint}</span>
          )}
        </p>
      )}
      <button type="button" className={`${primary} mt-2`} disabled={!chosen || record.isPending}
              onClick={() => record.mutate(
                { appointmentId: r.appointment_id, kind_code: chosen },
                {
                  onSuccess: (res) => {
                    const c = res.client;
                    onRecorded({
                      name: r.display_name,
                      kind: kinds.find((k) => k.code === chosen)?.label ?? chosen,
                      owes: !c || r.closed ? ''
                        : c.visits_per_year === 0 ? `The client now owes no visits (${c.visit_source ?? 'no source'}).`
                          : `The client owes ${c.visits_per_year} visits a year (${c.visit_source ?? 'no source'}).`,
                    });
                  },
                },
              )}>
        {record.isPending ? 'Recording…' : 'Record what it is'}
      </button>
    </li>
  );
}
